// @flow strict

import React from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createStructuredSelector } from 'reselect';

// Components
import RecommendationCardUi from './RecommendationCardUi';

// Services
import {
    selectIsCalculatingRecommendationImpact,
    selectCalculatedRecommendationImpact,
} from 'services/Recommendation/selectors';
import { calculateRecommendationImpact } from 'services/Recommendation/thunks';
import { selectUserTimezone } from 'services/Authentication/selectors';

// Types
import type {
    IntlType,
    ReduxDispatch,
    ImmutableList,
    LanguageCodeConstant,
} from 'types';
import type { ImmutableCircuit } from 'services/Circuit/types';
import type { ImmutableKPISetting } from 'services/KPISetting/types';
import type {
    ImmutableRecommendation,
    ImmutableRecommendationFeedback,
    RecommendationFeedbackTypeConstants,
} from 'services/Recommendation/types';

type Props = {
    intl: IntlType,
    kpiSettings: ImmutableList<ImmutableKPISetting>,
    circuit: ImmutableCircuit,
    userTimezone: string,
    userLocale: LanguageCodeConstant,
    isUserPM: boolean,

    feedback?: ?ImmutableRecommendationFeedback, // feedback currently being modified.
    loadingSubmittingFeedback?: boolean,

    recommendation: ImmutableRecommendation,
    cardIndex: number,
    readOnly: boolean,
    showCreatedAt?: boolean,
    recommendationSetWasSubmitted: boolean,
    showSubmissionTitle: boolean, // should we show if the recommendation was submitted in the title

    maxRecommendationCount?: number,

    datasetId?: number,
    hasModifiedItems?: boolean,
    isCalculatingImpact: boolean,
    calculatedImpact: ?ImmutableRecommendation,
    calculateRecommendationImpact: (
        circuitId: number,
        datasetId: number,
        recommendationId: number,
        data: any
    ) => void,
    onResetRecommendation?: ?(newRecommendation: ImmutableRecommendation) => void,

    onReorder?: ?(newOrder: number) => void,
    onDeleteRecommendation?: ?() => void,
    onChangeRationale?: ?(value: string) => void,
    onChangeIncludeInSubmission?: ?(isIncluded: boolean) => void,
    onChangeCuTransferImpact?: ?(cuTransferImpact: number) => void,
    onChangeOverallRecoveryImpact?: ?(overallRecoveryImpact: number) => void,

    onChangeItemToCustom?: ?(itemId: number) => void,
    onChangeItemKPI?: ?(itemId: number, newKPIId: number) => void,
    onChangeItemValue?: ?(itemId: number, newValue: number | string) => void,
    onAddItem?: ?() => void,
    onRemoveItem?: ?(itemId: number) => void,

    onChangeFeedback?: ?(
        recommendationId: number,
        feedbackType: ?RecommendationFeedbackTypeConstants,
        comment: ?string
    ) => void,
};

type State = {
    didImpactConverge: ?boolean,
    disableCalculateButton: ?boolean,
};

/**
 * Recommendation Sidebar component
 */
class RecommendationCard extends React.PureComponent<Props, State> {
    static defaultProps = {
        feedback: null,
        loadingSubmittingFeedback: false,
        maxRecommendationCount: 0,
        onChangeFeedback: null,
        onChangeIncludeInSubmission: null,
        onChangeRationale: null,
        onDeleteRecommendation: null,
        onReorder: null,

        onChangeItemToCustom: null,
        onChangeItemKPI: null,
        onChangeItemValue: null,
        onAddItem: null,
        onRemoveItem: null,
    };

    state = {
        didImpactConverge: null,
        disableCalculateButton: false,
    };

    render() {
        return <RecommendationCardUi
            {...this.props}
        />;
    }
}

const mapStateToProps = (_, ownProps: Props) =>
    createStructuredSelector({
        isCalculatingImpact: selectIsCalculatingRecommendationImpact(
            ownProps.recommendation.get('id')
        ),
        calculatedImpact: selectCalculatedRecommendationImpact(ownProps.recommendation.get('id')),
        userTimezone: selectUserTimezone(),
    });

const mapDispatchToProps = (dispatch: ReduxDispatch) =>
    bindActionCreators(
        {
            calculateRecommendationImpact,
        },
        dispatch
    );

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(RecommendationCard));
